// src/components/admin/VideoManager.jsx
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit2, User, Search, X, Video } from 'lucide-react';
import { ref as dbRef, set, remove, push } from 'firebase/database';

export default function VideoManager({ db, showToast, videos, currentUser }) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', url: '' });
  const [editingId, setEditingId] = useState(null);
  
  // STATE FOR SEARCH, SORT, AND PAGINATION
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const videosPerPage = 10;

  // Helper to extract YouTube Thumbnail from URL
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    try {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        return `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg`;
      }
    } catch (err) {
      console.error('Failed to parse YouTube thumbnail', err);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.url) {
      showToast('Please fill all fields', 'error');
      return;
    }
    
    if (!currentUser) {
        showToast('Authentication error. Please re-login.', 'error');
        return;
    }

    const userMetadata = {
        uploaderEmail: currentUser.email,
        uploaderName: currentUser.displayName || 'Admin',
        uploadedAt: new Date().toISOString(),
    };

    try {
      if (editingId) {
        // UPDATE OPERATION
        const videoToUpdate = videos.find(v => v.key === editingId);
        
        await set(dbRef(db, `content/videos/${editingId}`), {
          name: formData.name,
          url: formData.url,
          uploaderEmail: videoToUpdate?.uploaderEmail || 'Unknown',
          uploaderName: videoToUpdate?.uploaderName || 'Unknown',
          uploadedAt: videoToUpdate?.uploadedAt || new Date().toISOString(),
          updatedByEmail: currentUser.email,
          updatedByName: currentUser.displayName || 'Admin',
          updatedAt: new Date().toISOString(),
        });
        showToast('Video updated successfully!');
      } else {
        // CREATE OPERATION
        const newVideoRef = push(dbRef(db, 'content/videos'));
        
        await set(newVideoRef, {
          name: formData.name,
          url: formData.url,
          ...userMetadata
        });
        showToast('Video added successfully!');
      }
      setFormData({ name: '', url: '' });
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      console.error('Error saving video:', err);
      showToast('Error saving video', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await remove(dbRef(db, `content/videos/${id}`));
        showToast('Video deleted successfully!');
      } catch (err) {
        console.error('Error deleting video:', err);
        showToast('Error deleting video', 'error');
      }
    }
  };

  const handleEdit = (video) => {
    setFormData({ name: video.name, url: video.url });
    setEditingId(video.key); 
    setShowForm(true);
  };
  
  const videosWithKeys = videos.map((video, index) => ({
      ...video,
      key: video.key || index
  }));

  // --- SORTING LOGIC ---
  const requestSort = (key) => {
      let direction = 'ascending';
      if (sortConfig.key === key && sortConfig.direction === 'ascending') {
          direction = 'descending';
      }
      setSortConfig({ key, direction });
      setCurrentPage(1);
  };

  const getSortIndicator = (key) => {
      if (sortConfig.key !== key) return null;
      return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  const sortedVideos = useMemo(() => {
      let sortableItems = [...videosWithKeys];
      if (sortConfig.key) {
          sortableItems.sort((a, b) => {
              const aVal = String(a[sortConfig.key] || '').toLowerCase();
              const bVal = String(b[sortConfig.key] || '').toLowerCase();
              
              if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
              if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
              return 0;
          });
      }
      return sortableItems;
  }, [videosWithKeys, sortConfig]);

  // --- SEARCH/FILTERING LOGIC ---
  const filteredAndSortedVideos = useMemo(() => {
      return sortedVideos.filter(video => {
          const lowerCaseSearch = searchTerm.toLowerCase();
          return (
              String(video.name || '').toLowerCase().includes(lowerCaseSearch) ||
              String(video.url || '').toLowerCase().includes(lowerCaseSearch) ||
              String(video.uploaderName || '').toLowerCase().includes(lowerCaseSearch) ||
              String(video.uploaderEmail || '').toLowerCase().includes(lowerCaseSearch)
          );
      });
  }, [sortedVideos, searchTerm]);

  // --- PAGINATION LOGIC ---
  const indexOfLastVideo = currentPage * videosPerPage;
  const indexOfFirstVideo = indexOfLastVideo - videosPerPage;
  const currentVideos = filteredAndSortedVideos.slice(indexOfFirstVideo, indexOfLastVideo);
  const totalPages = Math.ceil(filteredAndSortedVideos.length / videosPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div>
      {/* Add Video Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({ name: '', url: '' });
        }}
        className="mb-8 flex items-center gap-2 px-6 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-medium transition shadow-md"
      >
        <Plus size={20} /> Add Video
      </motion.button>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#16161b] rounded-lg shadow-lg p-6 mb-8 border border-[#33333d]"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[#c9c9d4] font-medium mb-2">Video Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Ikaw Ang Kusog"
                className="w-full px-4 py-2 bg-[#1e1e25] border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#bd9f67] text-[#c9c9d4]"
              />
            </div>

            <div>
              <label className="block text-[#c9c9d4] font-medium mb-2">YouTube URL</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full px-4 py-2 bg-[#1e1e25] border border-[#33333d] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#bd9f67] text-[#c9c9d4]"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-medium transition"
              >
                {editingId ? 'Update Video' : 'Add Video'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ name: '', url: '' });
                }}
                className="flex-1 px-4 py-2 bg-[#1e1e25] hover:bg-[#26262d] text-[#f9f9f9] rounded-lg font-medium transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Search Bar */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
            <Search className="text-[#bd9f67]" size={18} />
          </div>
          <input
            type="text"
            placeholder="Search videos by name, URL, or uploader..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-10 py-3 bg-[#16161b] text-[#f9f9f9] placeholder-[#7d7d8a] rounded-lg border border-[#2a2a33] focus:border-[#bd9f67] focus:ring-1 focus:ring-[#bd9f67] focus:outline-none transition-all shadow-inner text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#7d7d8a] hover:text-[#f9f9f9] transition"
              title="Clear search"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div className="flex items-center text-xs text-[#7d7d8a] bg-[#16161b] px-3 py-2.5 rounded-lg border border-[#2a2a33] self-start md:self-auto">
          Showing <span className="font-semibold text-[#bd9f67] mx-1">{filteredAndSortedVideos.length}</span> of {videos.length} videos
        </div>
      </div>

      {/* Video List - Table View */}
      <div className="bg-[#16161b] rounded-lg shadow-lg overflow-x-auto border border-[#bd9f67]/15">
        <table className="min-w-full divide-y divide-[#2a2a33]">
          <thead className="bg-[#243137]">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#bd9f67] uppercase tracking-wider">
                Preview
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-[#bd9f67] uppercase tracking-wider cursor-pointer hover:bg-[#1e1e25] transition"
                onClick={() => requestSort('name')}
              >
                Name {getSortIndicator('name')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-[#bd9f67] uppercase tracking-wider cursor-pointer hover:bg-[#1e1e25] transition"
                onClick={() => requestSort('url')}
              >
                URL {getSortIndicator('url')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-[#bd9f67] uppercase tracking-wider cursor-pointer hover:bg-[#1e1e25] transition"
                onClick={() => requestSort('uploaderName')}
              >
                Uploader / Last Update {getSortIndicator('uploaderName')}
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#16161b] divide-y divide-[#2a2a33]">
            {currentVideos.map((video) => {
              const thumbnailUrl = getYouTubeThumbnail(video.url);

              return (
                <tr key={video.key} className="hover:bg-[#243137]">
                  {/* PREVIEW COLUMN */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {thumbnailUrl ? (
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-24 h-14 relative rounded-md overflow-hidden border border-[#2a2a33] hover:border-[#bd9f67] transition group"
                      >
                        <img
                          src={thumbnailUrl}
                          alt={video.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-200"
                        />
                      </a>
                    ) : (
                      <div className="w-24 h-14 bg-[#1e1e25] border border-[#2a2a33] rounded-md flex items-center justify-center text-[#7d7d8a]">
                        <Video size={20} />
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#f9f9f9]">
                    {video.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#bd9f67] hover:text-[#d0b07a]">
                    <a href={video.url} target="_blank" rel="noopener noreferrer" className="truncate block max-w-xs">
                      {video.url}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#7d7d8a]">
                    <div className="font-medium text-[#f9f9f9] flex items-center gap-1">
                      <User size={14} className="text-[#bd9f67]" />
                      <span className="font-semibold">Uploader:</span> 
                      {video.uploaderName || video.uploaderEmail || 'Unknown'}
                    </div>
                    {video.updatedByName && (
                      <div className="text-xs mt-1 text-[#7d7d8a] flex items-center gap-1">
                          <Edit2 size={12} className="text-[#bd9f67]" />
                          <span className="font-semibold">Updated:</span>
                          {video.updatedByName} on {new Date(video.updatedAt).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(video)}
                      className="text-[#bd9f67] hover:text-[#d0b07a] mr-3 transition"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(video.key)}
                      className="text-red-500 hover:text-red-400 transition"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-sm text-[#c9c9d4]">
            Showing {indexOfFirstVideo + 1} to {Math.min(indexOfLastVideo, filteredAndSortedVideos.length)} of {filteredAndSortedVideos.length} results
          </span>
          <div className="flex space-x-1.5">
            {pageNumbers.map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`px-3.5 py-1.5 text-sm font-medium rounded-lg transition ${
                  currentPage === number 
                    ? 'bg-[#bd9f67] text-[#243137] shadow-sm' 
                    : 'bg-[#1e1e25] text-[#c9c9d4] hover:bg-[#26262d] border border-[#2a2a33]'
                }`}
              >
                {number}
              </button>
            ))}
          </div>
        </div>
      )}

      {filteredAndSortedVideos.length === 0 && (
        <div className="text-center py-12 bg-[#16161b] rounded-lg border border-[#2a2a33] mt-4">
          <Search className="mx-auto text-[#7d7d8a] mb-3 opacity-50" size={32} />
          <p className="text-[#c9c9d4] font-medium">No matching videos found</p>
          <p className="text-xs text-[#7d7d8a] mt-1">Try adjusting your search terms or filters.</p>
        </div>
      )}
    </div>
  );
}